import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Upload, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', excerpt: '', image_url: '', is_published: false, lang: 'fr' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/blog/posts').then(r => setPosts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ title: '', content: '', excerpt: '', image_url: '', is_published: false, lang: 'fr' });
    setDialog(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    setForm({ title: post.title, content: post.content, excerpt: post.excerpt || '', image_url: post.image_url || '', is_published: post.is_published, lang: post.lang || 'fr' });
    setDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/blog/posts/${editing.post_id}`, form);
        toast.success('Article mis a jour');
      } else {
        await api.post('/blog/posts', form);
        toast.success('Article cree');
      }
      setDialog(false);
      load();
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await api.delete(`/blog/posts/${postId}`);
      toast.success('Article supprime');
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/cms/upload', formData);
        setForm(prev => ({ ...prev, image_url: res.data.url }));
        toast.success('Image uploadee');
      } catch {
        toast.error('Erreur upload');
      }
    };
    input.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{posts.length} article(s)</p>
        <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={openNew} data-testid="add-blog-post-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvel article
        </Button>
      </div>

      <Card className="border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titre</TableHead>
                <TableHead>Langue</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Date</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map(post => (
                <TableRow key={post.post_id}>
                  <TableCell className="font-medium">{post.title}</TableCell>
                  <TableCell><Badge variant="outline">{post.lang === 'fr' ? 'FR' : 'EN'}</Badge></TableCell>
                  <TableCell>
                    {post.is_published
                      ? <Badge className="bg-green-100 text-green-800">Publie</Badge>
                      : <Badge className="bg-gray-100 text-gray-800">Brouillon</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{post.created_at ? new Date(post.created_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(post)} data-testid={`edit-post-${post.post_id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(post.post_id)} data-testid={`delete-post-${post.post_id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {posts.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Aucun article</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editing ? 'Modifier l\'article' : 'Nouvel article'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="blog-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Extrait</Label>
              <Textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} rows={2} placeholder="Court resume de l'article..." data-testid="blog-excerpt-input" />
            </div>
            <div className="space-y-2">
              <Label>Contenu *</Label>
              <Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={8} data-testid="blog-content-input" />
            </div>
            <div className="space-y-2">
              <Label>Image</Label>
              <div className="flex gap-2">
                <Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="flex-1" placeholder="URL de l'image" data-testid="blog-image-input" />
                <Button variant="outline" size="sm" onClick={handleImageUpload}><Upload className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="flex gap-4 items-center">
              <div className="space-y-2 flex-1">
                <Label>Langue</Label>
                <Select value={form.lang} onValueChange={v => setForm({ ...form, lang: v })}>
                  <SelectTrigger data-testid="blog-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Francais</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={form.is_published} onCheckedChange={v => setForm({ ...form, is_published: v })} data-testid="blog-published-switch" />
                <Label>Publier</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Annuler</Button>
            <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleSave} disabled={saving || !form.title || !form.content} data-testid="save-blog-post-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editing ? 'Modifier' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
